import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import ErrorDisplay from '../error-display/error-display';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const BlogEditor = () => {
  const { blogId } = useParams();
  const isEditing = !!blogId;
  const navigate = useNavigate();
  const { currentUser, isAdmin } = useAuth();
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // State variables for image and event details
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [eventDate, setEventDate] = useState(null);
  const [eventEndDate, setEventEndDate] = useState(null);
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    // If editing an existing blog, fetch its data
    if (isEditing) {
      const fetchBlog = async () => {
        setLoading(true);
        try {
          const blogRef = doc(db, 'blogs', blogId);
          const blogSnap = await getDoc(blogRef);
          
          if (blogSnap.exists()) {
            const blogData = blogSnap.data();
            setTitle(blogData.title);
            setContent(blogData.content);
            setIsPublished(blogData.isPublished);
            
            // Set image URL if exists
            if (blogData.imageUrl) {
              setCurrentImageUrl(blogData.imageUrl);
              setImagePreview(blogData.imageUrl);
            }
            
            // Set event date if exists
            if (blogData.eventDate) {
              setEventDate(blogData.eventDate.toDate());
            }
            
            // Set event end date if exists
            if (blogData.eventEndDate) {
              setEventEndDate(blogData.eventEndDate.toDate());
            }
          } else {
            setError('Blog post not found');
          }
        } catch (err) {
          setError('Error fetching blog: ' + err.message);
        } finally {
          setLoading(false);
        }
      };
      
      fetchBlog();
    }
  }, [blogId, isEditing]);
  
  // Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Remove selected image
  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    if (isEditing) {
      setCurrentImageUrl('');
    }
  };
  
  // Upload image to Firebase Storage
  const uploadImage = async () => {
    if (!imageFile) return currentImageUrl;
    
    try {
      // Sanitize filename to avoid invalid characters
      const sanitizedFileName = imageFile.name.replace(/[#[\]*?]/g, '_');
      
      // Create a reference to the storage location
      const storageRef = ref(storage, `blog-images/${Date.now()}_${sanitizedFileName}`);
      
      // Upload the file
      await uploadBytes(storageRef, imageFile);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      // If updating and there's an existing image, delete the old one
      if (isEditing && currentImageUrl) {
        try {
          // Extract the path from the URL
          const oldImageRef = ref(storage, currentImageUrl);
          await deleteObject(oldImageRef);
        } catch (error) {
          console.error("Error deleting old image:", error);
        }
      }
      
      return downloadURL;
    } catch (error) {
      console.error("Upload error:", error);
      
      // Handle specific error types
      if (error.code === 'storage/cors-config-required') {
        throw new Error('CORS configuration required. Please contact administrator.');
      } else if (error.code === 'storage/unauthenticated') {
        throw new Error('Authentication required. Please log in again.');
      } else if (error.code === 'storage/unauthorized') {
        throw new Error('You do not have permission to upload images.');
      } else if (error.code === 'storage/canceled') {
        throw new Error('Upload was canceled.');
      } else if (error.code === 'storage/unknown') {
        throw new Error('An unknown error occurred during upload.');
      } else {
        throw new Error(`Upload failed: ${error.message}`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAdmin) {
      setError('You do not have permission to publish blogs');
      return;
    }
    
    if (!title.trim() || !content.trim()) {
      setError('Title and content are required');
      return;
    }
    
    // Validate event dates if both are provided
    if (eventDate && eventEndDate) {
      if (eventEndDate < eventDate) {
        setError('Event end date cannot be before event start date');
        return;
      }
    }
    
    setLoading(true);
    setError('');
    setDateError('');
    setSuccess('');
    
    try {
      // Upload image if selected
      let imageUrl = currentImageUrl;
      if (imageFile) {
        imageUrl = await uploadImage();
      }
      
      const blogData = {
        title,
        content,
        isPublished,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
        imageUrl: imageUrl || null,
        eventDate: eventDate ? new Date(eventDate) : null,
        eventEndDate: eventEndDate ? new Date(eventEndDate) : null
      };
      
      if (isEditing) {
        // Update existing blog
        const blogRef = doc(db, 'blogs', blogId);
        await updateDoc(blogRef, blogData);
        setSuccess('Blog updated successfully');
      } else {
        // Create new blog
        const newBlogRef = collection(db, 'blogs');
        await addDoc(newBlogRef, {
          ...blogData,
          id: newBlogRef.id,
          createdAt: serverTimestamp(),
          createdBy: currentUser.uid
        });
        setSuccess('Blog created successfully');
        
        // Reset form after creating new blog
        setTitle('');
        setContent('');
        setIsPublished(false);
        setImageFile(null);
        setImagePreview('');
        setEventDate(null);
        setEventEndDate(null);
        
        // Navigate to the blog list
        setTimeout(() => {
          navigate('/admin/blogs');
        }, 2000);
      }
    } catch (err) {
      setError('Error saving blog: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex align-items-center mb-3">
        <button
          type="button"
          className="btn btn-link text-decoration-none p-0 me-3"
          onClick={() => navigate('/admin/blogs')}
          style={{ fontSize: '1.5rem' }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2 className="mb-0">{isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}</h2>
      </div>
      
      {error && <ErrorDisplay message={error} />}
      {success && <ErrorDisplay message={success} type="info" />}
      
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="title" className="form-label">Title</label>
          <input
            type="text"
            className="form-control"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={loading}
            required
          />
        </div>
        
        {/* Image upload field (new) */}
        <div className="mb-3">
          <label htmlFor="image" className="form-label">Featured Image</label>
          <input
            type="file"
            className="form-control"
            id="image"
            accept="image/*"
            onChange={handleImageChange}
            disabled={loading}
          />
          <small className="text-muted">Recommended size: 1200 x 630 pixels</small>
          
          {/* Image preview */}
          {imagePreview && (
            <div className="mt-2">
              <img
                src={imagePreview}
                alt="Preview"
                className="img-thumbnail"
                style={{ maxHeight: '200px' }}
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-danger ms-2"
                onClick={handleRemoveImage}
                disabled={loading}
              >
                Remove Image
              </button>
            </div>
          )}
        </div>
        
        {/* Event details section */}
        <div className="mb-3">
          <label className="form-label">Event Details (optional)</label>
          <div className="card p-3">
            <div className="row">
              <div className="col-md-6">
                <label htmlFor="eventDate" className="form-label">Start Date & Time</label>
                <DatePicker
                  id="eventDate"
                  className="form-control"
                  selected={eventDate}
                  onChange={date => {
                    setEventDate(date);
                    // Clear date error when start date changes
                    setDateError('');
                    // If end date exists and is now invalid, clear it
                    if (eventEndDate && date > eventEndDate) {
                      setEventEndDate(null);
                    }
                  }}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="MMMM d, yyyy h:mm aa"
                  placeholderText="Select event start date and time"
                  disabled={loading}
                />
              </div>
              
              <div className="col-md-6">
                <label htmlFor="eventEndDate" className="form-label">End Date & Time (optional)</label>
                <DatePicker
                  id="eventEndDate"
                  className="form-control"
                  selected={eventEndDate}
                  onChange={date => {
                    setEventEndDate(date);
                    // Validate end date is after start date
                    if (date && eventDate && date < eventDate) {
                      setDateError('End date & time must be after start date & time');
                    } else {
                      setDateError('');
                    }
                  }}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="MMMM d, yyyy h:mm aa"
                  placeholderText="Select event end date and time"
                  disabled={loading || !eventDate}
                  minDate={eventDate}
                  minTime={
                    eventDate && eventEndDate &&
                    eventDate.toDateString() === eventEndDate.toDateString() ?
                    new Date(
                      eventDate.getFullYear(),
                      eventDate.getMonth(),
                      eventDate.getDate(),
                      eventDate.getHours(),
                      eventDate.getMinutes()
                    ) :
                    undefined
                  }
                />
                {!eventDate && (
                  <small className="text-muted">Set start date & time first</small>
                )}
              </div>
            </div>
            {dateError && (
              <ErrorDisplay message={dateError} className="mt-2 mb-0 py-2" />
            )}
            <small className="text-muted mt-2">Set start date/time and optional end date/time if this blog post is for an event</small>
          </div>
        </div>
        
        {/* Rich text editor with Quill */}
        <div className="mb-3">
          <label htmlFor="content" className="form-label">Content</label>
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            readOnly={loading}
            modules={{
              toolbar: [
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                [{ 'color': [] }, { 'background': [] }],
                ['link'],
                ['clean']
              ],
            }}
            style={{ height: '300px', marginBottom: '50px' }}
          />
        </div>
        
        <div className="mb-3 form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="isPublished"
            checked={isPublished}
            onChange={(e) => setIsPublished(e.target.checked)}
            disabled={loading}
          />
          <label className="form-check-label" htmlFor="isPublished">Publish</label>
        </div>
        
        <div className="d-flex gap-2">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <LoadingSpinner size="small" className="me-2" text='Saving...' />
            ) : (
              isEditing ? 'Update Blog' : 'Create Blog'
            )}
          </button>
          
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/admin/blogs')}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogEditor;